import { DOCUMENTS_COPY } from '../i18n/documents';

export const DOCUMENT_TYPE_OPTIONS = [
    { value: 'driver_licence', key: 'driverLicence' },
    { value: 'permit', key: 'permit' },
    { value: 'other', key: 'other' },
];

export const LEGACY_DOCUMENT_KIND_TO_NEW = {
    id: 'driver_licence',
    certificate: 'permit',
    medical: 'other',
};

export const REQUIRED_DOCUMENT_KINDS = ['driver_licence', 'permit'];

export const DOCUMENT_STATUS_META = {
    PENDING: { label: 'Pending Review', badgeClass: 'bg-yellow-100 text-yellow-800' },
    APPROVED: { label: 'Approved', badgeClass: 'bg-green-100 text-green-800' },
    REJECTED: { label: 'Needs Update', badgeClass: 'bg-red-100 text-red-800' },
};

export function normalizeDocumentKind(kind) {
    if (!kind) return '';
    return LEGACY_DOCUMENT_KIND_TO_NEW[kind] || kind;
}

export function getDocumentStatusMeta(status) {
    const normalized = (status || 'PENDING').toUpperCase();
    return DOCUMENT_STATUS_META[normalized] || DOCUMENT_STATUS_META.PENDING;
}

export function buildDocumentRequirementsSummary(documents = []) {
    const summary = {};
    REQUIRED_DOCUMENT_KINDS.forEach((kind) => {
        summary[kind] = { status: 'MISSING', documents: [], latestDocument: null };
    });

    documents.forEach((doc) => {
        const normalizedKind = normalizeDocumentKind(doc.kind);
        if (!REQUIRED_DOCUMENT_KINDS.includes(normalizedKind)) {
            return;
        }

        summary[normalizedKind].documents.push(doc);
    });

    REQUIRED_DOCUMENT_KINDS.forEach((kind) => {
        const entry = summary[kind];
        if (!entry.documents.length) {
            return;
        }

        const latest = entry.documents.reduce((acc, current) => {
            const timestamp = new Date(current.uploadedAt || 0).getTime() || 0;
            if (!acc || timestamp > acc.time) {
                return { doc: current, time: timestamp };
            }
            return acc;
        }, null);

        entry.latestDocument = latest?.doc || null;
        const latestStatus = (latest?.doc?.status || 'PENDING').toUpperCase();

        if (latestStatus === 'APPROVED') {
            entry.status = 'APPROVED';
        } else if (latestStatus === 'REJECTED') {
            entry.status = 'REJECTED';
        } else {
            entry.status = 'PENDING';
        }
    });

    return summary;
}

export function studentDocumentsRequireAttention(summary) {
    return REQUIRED_DOCUMENT_KINDS.some((kind) => {
        const entry = summary[kind];
        return !entry || entry.status !== 'APPROVED';
    });
}

export function countPendingRequiredDocuments(documents = []) {
    return documents.reduce((total, doc) => {
        const normalizedKind = normalizeDocumentKind(doc.kind);
        if (!REQUIRED_DOCUMENT_KINDS.includes(normalizedKind)) {
            return total;
        }

        const normalizedStatus = (doc.status || 'PENDING').toUpperCase();
        if (normalizedStatus === 'PENDING') {
            return total + 1;
        }

        return total;
    }, 0);
}

export function countPendingAppointments(appointments = []) {
    return appointments.reduce((total, appointment) => {
        const normalizedStatus = (appointment?.status || '').toUpperCase();
        return normalizedStatus === 'PENDING' ? total + 1 : total;
    }, 0);
}

export function buildDocumentTypeLabelMap(optionsOverride) {
    const labels = DOCUMENT_TYPE_OPTIONS.reduce((acc, option) => {
        const label = optionsOverride?.[option.key] || DOCUMENTS_COPY.default.upload.options[option.key];
        acc[option.value] = label;
        return acc;
    }, {});
    Object.entries(LEGACY_DOCUMENT_KIND_TO_NEW).forEach(([legacy, modern]) => {
        if (labels[modern]) {
            labels[legacy] = labels[modern];
        }
    });
    return labels;
}

export function formatDocumentKindLabel(kind, labels) {
    if (!kind) return '';
    if (labels && labels[kind]) return labels[kind];
    const normalized = String(kind).replace(/[_-]+/g, ' ');
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
